import { z } from "zod";

/**
 * Contrat du module portefeuille de la console (spec du 13 septembre 2026, sections 1 et 2).
 *
 * Ce fichier appartient a l'app : il est sous `src/app/`, donc dans `_skip_if_exists`, donc
 * le template ne le pose pas et ne le reecrira jamais.
 *
 * Les noms de champs sont en francais parce que c'est le contrat de donnees, pas du code :
 * ce sont les memes mots que l'en-tete YAML de `00-fiche.md` et que `docs/idees/index.json`,
 * et la page les affiche tels quels. Deux exceptions de forme, assumees : `prochaineDate` et
 * `genere_le`. `prochaineDate` passe en camelCase parce que le pari est un objet reconstruit
 * par le backend (le fichier, lui, ecrit `prochaine_date`) ; `genere_le` garde son souligne
 * parce qu'il est recopie tel quel depuis `index.json`, sans transformation.
 */

/** Etape du cycle de vie, commune aux idees et aux opportunites du scout. */
export const ETAPES = [
  "idée",
  "triée",
  "grillée",
  "en test",
  "build",
  "live",
  "parquée",
  "tuée",
] as const;
export const etapeSchema = z.enum(ETAPES);
export type Etape = z.infer<typeof etapeSchema>;

/** Nature d'un geste qui n'attend que Nicolas. */
export const TYPES_PREREQUIS = [
  "secret",
  "dns",
  "compte",
  "achat",
  "decision",
  "lecture",
  "reglage",
] as const;
export const typePrerequisSchema = z.enum(TYPES_PREREQUIS);
export type TypePrerequis = z.infer<typeof typePrerequisSchema>;

export const sourceSchema = z.enum(["idee", "scout"]);
export type SourcePari = z.infer<typeof sourceSchema>;

export const prerequisSchema = z.object({
  quoi: z.string(),
  type: typePrerequisSchema,
  fait: z.boolean(),
  /** Date d'inscription du prerequis (AAAA-MM-JJ), ou null quand la skill ne l'a pas posee. */
  depuis: z.string().nullable(),
  /**
   * Nom du guide, dans `docs/idees/<slug>/gestes/<guide>.md`, ou null. `check` l'exige sur
   * un prerequis ouvert ; un prerequis deja fait peut rester sans guide (spec des gestes
   * guides, 17 septembre 2026, section 2).
   */
  guide: z.string().nullable(),
});
export type Prerequis = z.infer<typeof prerequisSchema>;

/**
 * Liens du pari. Champs facultatifs plutot que nullables : un lien absent ne s'affiche pas,
 * et le distinguer d'un lien vide n'apporterait rien a la page.
 */
export const liensSchema = z.object({
  /** Chemin du dossier relatif a `docs/idees/`, par exemple `cinepass/`. */
  dossier: z.string().optional(),
  issue: z.number().int().optional(),
  /** URL de preuve : la source de l'opportunite pour le scout. */
  preuve: z.string().optional(),
  ticket: z.string().optional(),
  /** Chemin de la spec depuis la racine du depot. */
  spec: z.string().optional(),
});
export type Liens = z.infer<typeof liensSchema>;

/** Ce que la page affiche, quelle que soit la source. */
export const pariSchema = z.object({
  /** `idee:<slug>` ou `scout:<identifiant court>`. Unique dans toute la liste. */
  id: z.string(),
  source: sourceSchema,
  titre: z.string(),
  /** A, B, C, D, E, S, promo, outil, ou la piste brute du scout. */
  piste: z.string().nullable(),
  etape: etapeSchema,
  score: z.number().nullable(),
  /** Plafond de score par la qualite de la preuve. Le scout ne l'enregistre pas : null. */
  plafond: z.number().nullable(),
  verdict: z.string().nullable(),
  pourquoi: z.string().nullable(),
  declencheur: z.string().nullable(),
  /** Date de relance ou de lecture (AAAA-MM-JJ). */
  prochaineDate: z.string().nullable(),
  liens: liensSchema,
  prerequis: z.array(prerequisSchema),
  /** Date de derniere mise a jour (AAAA-MM-JJ, ou horodatage pour le scout). */
  maj: z.string(),
});
export type Pari = z.infer<typeof pariSchema>;

/**
 * D'ou vient un geste : porte par un pari du portefeuille (le prerequis d'une fiche), ou
 * pose seul dans `docs/gestes/`. C'est ce qui decide de l'adresse de sa page, pas la
 * source du pari : un prerequis reste `idee` quel que soit le pari qui le porte.
 */
export const sourceGesteSchema = z.enum(["idee", "hors-idee"]);
export type SourceGeste = z.infer<typeof sourceGesteSchema>;

/**
 * Un geste encore ouvert : le prerequis d'une fiche, ou un geste hors idee. `fait` n'est
 * pas repete : par construction il vaut false pour tout ce qui est dans cette liste.
 *
 * Le nom du type garde le mot `prerequis` parce que c'est le nom du champ de la reponse
 * et le libelle de la page ; les gestes hors idee y entrent avec `pari` et `titre` a null.
 */
export const prerequisOuvertSchema = z.object({
  /**
   * Ce que `npm run fait -- <cible>` attend : `<slug>/<guide>` pour le prerequis d'une
   * fiche, `gestes/<nom>` hors idee. Null quand le geste n'a pas de guide, ce que `check`
   * refuse sur un geste ouvert : la ligne n'est alors pas cliquable, plutot que de mener
   * a un 404.
   */
  cible: z.string().nullable(),
  source: sourceGesteSchema,
  /** Identifiant du pari debloque par ce geste, ou null hors idee. */
  pari: z.string().nullable(),
  titre: z.string().nullable(),
  quoi: z.string(),
  type: typePrerequisSchema,
  depuis: z.string().nullable(),
  /** Nom du guide : `<guide>` dans une fiche, `<nom>` hors idee. */
  guide: z.string().nullable(),
  /** Numero du chantier GitHub dont le geste depend, ou null. */
  issue: z.number().int().nullable(),
});
export type PrerequisOuvert = z.infer<typeof prerequisOuvertSchema>;

/** Un geste tel que sa page le montre : comme en liste, plus son statut. */
export const gesteSchema = prerequisOuvertSchema.extend({ fait: z.boolean() });
export type Geste = z.infer<typeof gesteSchema>;

/** Le guide d'un geste : le markdown du fichier, sans son en-tete. */
export const guideSchema = z.object({
  markdown: z.string(),
  /** Date de derniere verification (AAAA-MM-JJ), ou null quand l'en-tete ne la porte pas. */
  verifieLe: z.string().nullable(),
});
export type Guide = z.infer<typeof guideSchema>;

/** Le guide d'une relance : le generique, ou celui de l'idee quand elle en a un. */
export const guideRelanceSchema = guideSchema.extend({
  /** Vrai quand `docs/idees/<slug>/gestes/relance.md` remplace le guide generique. */
  surcharge: z.boolean(),
});
export type GuideRelance = z.infer<typeof guideRelanceSchema>;

/** Un pari dont la date de relance est echue ou proche. */
export const relanceSchema = z.object({
  pari: z.string(),
  titre: z.string(),
  prochaineDate: z.string(),
  /** Jours restants. Negatif = la date est passee. */
  jours: z.number().int(),
});
export type Relance = z.infer<typeof relanceSchema>;

/** Reponse de `GET /api/portefeuille`. */
export const portefeuilleSchema = z.object({
  paris: z.array(pariSchema),
  prerequis: z.array(prerequisOuvertSchema),
  relances: z.array(relanceSchema),
  /** Pannes d'alimenteur, en clair. Une liste vide veut dire que les trois ont repondu. */
  avertissements: z.array(z.string()),
  /** Date de generation de `docs/idees/index.json`, pour voir l'age des donnees. */
  genere_le: z.string().nullable(),
});
export type Portefeuille = z.infer<typeof portefeuilleSchema>;

/** Un fichier markdown du dossier d'une idee. */
export const fichierSchema = z.object({
  /** Nom du fichier, par exemple `01-grill.md`. */
  nom: z.string(),
  /** Markdown brut. L'en-tete YAML est retire de la fiche, il est deja dans le pari. */
  markdown: z.string(),
});
export type Fichier = z.infer<typeof fichierSchema>;

/** Reponse de `GET /api/portefeuille/idees/:slug`. */
export const ideeDetailSchema = z.object({
  pari: pariSchema,
  fichiers: z.array(fichierSchema),
});
export type IdeeDetail = z.infer<typeof ideeDetailSchema>;

/**
 * Reponse des deux routes d'un guide de geste : `GET /api/portefeuille/idees/:slug/gestes/:nom`
 * pour le prerequis d'une fiche, `GET /api/portefeuille/gestes/:nom` hors idee.
 */
export const gesteDetailSchema = z.object({
  geste: gesteSchema,
  guide: guideSchema,
});
export type GesteDetail = z.infer<typeof gesteDetailSchema>;

/** Reponse de `GET /api/portefeuille/idees/:slug/relance` : le pari, puis son guide. */
export const relanceDetailSchema = z.object({
  pari: pariSchema,
  guide: guideRelanceSchema,
});
export type RelanceDetail = z.infer<typeof relanceDetailSchema>;

/**
 * La cible d'un geste, telle que `npm --prefix packages/idees run fait` l'attend :
 * `<slug>/<guide>` pour le prerequis d'une fiche, `gestes/<nom>` hors idee. Deux noms de
 * `packages/idees` (`NAME_RE`) de part et d'autre d'une seule barre : jamais un `..`, jamais
 * une majuscule, jamais une seconde barre.
 *
 * C'est la meme expression aux trois gardes du bouton « C'est fait » (cette console,
 * lab-tools, le workflow `geste-fait.yml`), recopiee et non importee : les trois vivent dans
 * trois paquets qui ne partagent rien, et chacune doit tenir seule (spec des gestes guides,
 * section 8). Les ecrire a l'identique est ce qui les rend comparables d'un coup d'oeil.
 */
export const CIBLE_GESTE = /^[a-z0-9][a-z0-9-]{0,63}\/[a-z0-9][a-z0-9-]{0,63}$/;

/** Corps de `POST /api/portefeuille/gestes/fait` : la cible, et rien d'autre. */
export const gesteFaitDemandeSchema = z.object({
  cible: z.string().regex(CIBLE_GESTE),
});
export type GesteFaitDemande = z.infer<typeof gesteFaitDemandeSchema>;

/**
 * Reponse 202 de `POST /api/portefeuille/gestes/fait` : le workflow est parti. Le geste, lui,
 * n'est pas encore coche : il le sera quand le commit du workflow aura ete pousse puis copie
 * sur le serveur par `docs-sync`, une a deux minutes plus tard.
 */
export const gesteFaitReponseSchema = z.object({
  cible: z.string(),
  /** Horodatage ISO de l'envoi, pris par le serveur : « demande envoyee a 14:32 ». */
  demandeLe: z.string(),
  /** Ce que lab-tools a repondu, en clair. */
  message: z.string(),
});
export type GesteFaitReponse = z.infer<typeof gesteFaitReponseSchema>;

/** Un critere de la grille du scout, avec son maximum. */
export const critereSchema = z.object({
  critere: z.string(),
  valeur: z.number().nullable(),
  maximum: z.number(),
});
export type Critere = z.infer<typeof critereSchema>;

export const preuveSchema = z.object({
  texte: z.string(),
  url: z.string().nullable(),
});
export type Preuve = z.infer<typeof preuveSchema>;

/** La carte complete d'une opportunite du scout. */
export const carteScoutSchema = z.object({
  id: z.string(),
  identifiantCourt: z.string(),
  titre: z.string(),
  resume: z.string().nullable(),
  niche: z.string().nullable(),
  canal: z.string().nullable(),
  piste: z.string().nullable(),
  /** D'ou vient l'opportunite (veille, suggestion, ...), pas la source du pari. */
  origine: z.string().nullable(),
  statut: z.string(),
  score: z.number().nullable(),
  grille: z.array(critereSchema),
  preuves: z.array(preuveSchema),
  planDeTest: z.string().nullable(),
  risques: z.array(z.string()),
  decision: z.string().nullable(),
  remarque: z.string().nullable(),
  decideLe: z.string().nullable(),
  ticket: z.string().nullable(),
  cadrage: z.string().nullable(),
  trouveLe: z.string().nullable(),
  coutEstimeCents: z.number().nullable(),
});
export type CarteScout = z.infer<typeof carteScoutSchema>;

/** Reponse de `GET /api/portefeuille/scout/:id`. */
export const scoutDetailSchema = z.object({
  pari: pariSchema,
  carte: carteScoutSchema,
});
export type ScoutDetail = z.infer<typeof scoutDetailSchema>;
